<template>
  <div class="gm-panel tool-panel">
    <h3 class="gm-title tool-panel-title">Editor Tools</h3>

    <!-- Mode Switch -->
    <div class="gm-tabs mode-switch">
      <button
        class="gm-tab"
        :class="{ 'is-active': editorMode === 'config' }"
        @click="changeMode('config')"
      >
        Map Config
      </button>
      <button
        class="gm-tab"
        :class="{ 'is-active': editorMode === 'availability' }"
        @click="changeMode('availability')"
      >
        Availability
      </button>
      <button
        class="gm-tab"
        :class="{ 'is-active': editorMode === 'object' }"
        @click="changeMode('object')"
      >
        Objects
      </button>
      <button
        class="gm-tab"
        :class="{ 'is-active': editorMode === 'path' }"
        @click="changeMode('path')"
      >
        Paths
      </button>
    </div>

    <div class="tools-list">
      <button
        v-for="tool in visibleTools"
        :key="tool.id"
        :class="['gm-btn', 'tool-button', { 'is-active': tool.id === selectedTool }]"
        @click="handleToolClick(tool.id)"
        :title="tool.description"
      >
        <span class="tool-name">{{ tool.name }}</span>
      </button>
    </div>

    <div class="tool-options">
      <h4 class="gm-section-title">Legend / View Options</h4>
      <label class="option-checkbox">
        <input type="checkbox" :checked="availabilityVisible" @change="toggleAvailability" />
        <span>Show Availability</span>
      </label>
      <label class="option-checkbox">
        <input type="checkbox" :checked="pathsVisible" @change="togglePaths" />
        <span>Show Paths</span>
      </label>
      <label class="option-checkbox">
        <input type="checkbox" :checked="objectsVisible" @change="toggleObjects" />
        <span>Show Objects</span>
      </label>
      <label class="option-checkbox">
        <input type="checkbox" :checked="gridVisible" @change="toggleGrid" />
        <span>Show Grid</span>
      </label>
      <label class="option-checkbox">
        <input type="checkbox" :checked="backgroundVisible" @change="toggleBackground" />
        <span>Show Background</span>
      </label>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ToolPanel',
  data() {
    return {
      tools: [
        {
          id: 'select',
          name: 'Select',
          icon: '👆',
          description: 'Select and inspect cells'
        },
        {
          id: 'availability',
          name: 'Availability',
          icon: '🚫',
          description: 'Toggle cell availability (spawning/movement)'
        },
        {
          id: 'fill-available',
          name: 'Fill All Available',
          icon: '✅',
          description: 'Fill all cells on current layer as available'
        },
        {
          id: 'fill-unavailable',
          name: 'Fill All Unavailable',
          icon: '🔒',
          description: 'Fill all cells on current layer as unavailable'
        },
        {
          id: 'flood-fill',
          name: 'Flood Fill',
          icon: '🪣',
          description: 'Fill bounded region with same availability as clicked cell'
        },
        {
          id: 'wall',
          // Internally still uses the 'wall' tool id, but in Path mode this
          // acts as the Path/Edge editing tool.
          name: 'Path / Edges',
          icon: '🧭',
          description: 'Edit movement paths and connections between cells'
        },
        {
          id: 'spawner-player',
          name: 'Player Spawner',
          icon: '👤',
          description: 'Place player spawn points'
        },
        {
          id: 'spawner-npc',
          name: 'NPC Spawner',
          icon: '👹',
          description: 'Place NPC/enemy spawn points'
        },
        {
          id: 'spawner-object',
          name: 'Object Spawner',
          icon: '📦',
          description: 'Place object spawn points'
        },
        {
          id: 'spawner-effect',
          name: 'Effect Spawner',
          icon: '✨',
          description: 'Place effect/trap spawn points'
        },
        {
          id: 'erase',
          name: 'Erase',
          icon: '🗑️',
          description: 'Remove spawners and objects'
        },
        {
          id: 'anchor-points',
          name: 'Set Anchor Points',
          icon: '📍',
          description: 'Set 4 corner anchor points to morph/transform the grid'
        },
        {
          id: 'auto-map-paths',
          name: 'Auto-Map Paths',
          icon: '🗺️',
          description: 'Automatically generate all movement paths for available cells'
        }
      ]
    };
  },
  computed: {
    ...mapGetters('tabletopEditor', [
      'selectedTool',
      'gridVisible',
      'backgroundVisible',
      'editorMode',
      'availabilityVisible',
      'pathsVisible',
      'objectsVisible'
    ]),

    visibleTools() {
      // Filter tools based on the current editor mode
      if (this.editorMode === 'config') {
        // Map Config mode: select and anchor points for grid transformation
        return this.tools.filter(tool => tool.id === 'select' || tool.id === 'anchor-points');
      }

      if (this.editorMode === 'availability') {
        return this.tools.filter(tool =>
          tool.id === 'select' ||
          tool.id === 'availability' ||
          tool.id === 'fill-available' ||
          tool.id === 'fill-unavailable' ||
          tool.id === 'flood-fill'
        );
      }

      if (this.editorMode === 'object') {
        return this.tools.filter(tool =>
          tool.id === 'select' ||
          tool.id === 'spawner-player' ||
          tool.id === 'spawner-npc' ||
          tool.id === 'spawner-object' ||
          tool.id === 'spawner-effect' ||
          tool.id === 'erase'
        );
      }

      // Path mode: select + path/edges tool (internally 'wall') + auto-map
      if (this.editorMode === 'path') {
        return this.tools.filter(tool =>
          tool.id === 'select' || tool.id === 'wall' || tool.id === 'auto-map-paths'
        );
      }

      // Fallback: show all tools
      return this.tools;
    }
  },
  methods: {
    ...mapActions('tabletopEditor', [
      'selectTool',
      'toggleGridVisibility',
      'toggleBackgroundVisibility',
      'setEditorMode',
      'toggleAvailabilityVisibility',
      'togglePathsVisibility',
      'toggleObjectsVisibility',
      'fillAllCells',
      'autoGenerateEdges'
    ]),
    handleToolClick(toolId) {
      // Handle fill tools as immediate actions
      if (toolId === 'fill-available') {
        this.fillAllCells({ passable: true });
      } else if (toolId === 'fill-unavailable') {
        this.fillAllCells({ passable: false });
      } else if (toolId === 'auto-map-paths') {
        this.autoGenerateEdges();
      } else {
        // For other tools, just select them
        this.selectTool(toolId);
      }
    },
    changeMode(mode) {
      this.setEditorMode(mode);
    },
    toggleGrid() {
      this.toggleGridVisibility();
    },
    toggleBackground() {
      this.toggleBackgroundVisibility();
    },
    toggleAvailability() {
      this.toggleAvailabilityVisibility();
    },
    togglePaths() {
      this.togglePathsVisibility();
    },
    toggleObjects() {
      this.toggleObjectsVisibility();
    }
  }
};
</script>

<style scoped>
.tool-panel-title {
  margin: 0 0 var(--gm-gap);
}

.mode-switch {
  margin-bottom: var(--gm-gap);
}

.mode-switch .gm-tab {
  flex: 1;
  text-align: center;
}

.tools-list {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap-lg);
}

.tool-button {
  justify-content: flex-start;
  width: 100%;
}

.tool-name {
  flex: 1;
  text-align: left;
}

.tool-options {
  border-top: 1px solid var(--gm-border-dim);
  padding-top: var(--gm-gap);
}

.option-checkbox {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap-sm) 0;
  cursor: pointer;
  user-select: none;
}

.option-checkbox input[type="checkbox"] {
  width: 1.1rem;
  height: 1.1rem;
  cursor: pointer;
  accent-color: var(--gm-accent);
}

.option-checkbox span {
  font-size: 0.8rem;
  color: var(--gm-text);
}

.option-checkbox:hover span {
  color: var(--gm-accent);
}
</style>
